import React, { useState, Children } from 'react';
import { Container } from './ui/Container';
import { Button } from './ui/Button';
import { ShieldCheck, CheckCircle, Phone, MapPin, Clock, Send } from 'lucide-react';
interface FormState {
  name: string;
  email: string;
  phone: string;
  program: string;
  message: string;
}
const programs = ['Not sure yet', "Children's Classes", 'Children / Adult Classes', 'Cardio Kickboxing', 'FIT KIDS Program', 'Advanced Classes', 'Sparring', 'Demo Team', 'Community Engagement'];
const trustBadges = [{
  label: 'Kukkiwon',
  sub: 'Affiliated'
}, {
  label: 'WTF',
  sub: 'World TKD Fed.'
}, {
  label: 'USNTF',
  sub: 'US National TKD'
}, {
  label: 'ITSO',
  sub: 'Illinois TKD Org.'
}];
const bullets = ['Speed and agility training', 'Cardiovascular workouts', 'Self defense', 'Forms', 'Sparring'];
const steps = [{
  num: '1',
  label: 'Choose a program'
}, {
  num: '2',
  label: 'Request your free week or call us'
}, {
  num: '3',
  label: 'Visit us and start training'
}];
export function ImmediateOfferSection() {
  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    phone: '',
    program: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <section id="signup" className="py-20 bg-white border-b border-gray-200" data-id="element-172">
      <Container data-id="element-173">
        <div className="flex flex-col lg:flex-row gap-12 xl:gap-20 items-start" data-id="element-174">
          {/* ── LEFT COLUMN ── */}
          <div className="flex-1 min-w-0" data-id="element-175">
            <h2 className="text-3xl md:text-4xl font-extrabold text-nav font-heading uppercase tracking-tight mb-3" data-id="element-176">
              Start with a One Week Free Trial
            </h2>
            <div className="w-20 h-1 bg-accent mb-6" data-id="element-177"></div>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-178">
              Taekwondo classes for all ages and skill levels in Round Lake,
              Illinois — from your child's first class to advanced black belt
              training.
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3 mb-10" data-id="element-179">
              {trustBadges.map(b => <div key={b.label} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-md px-3 py-2" data-id="element-180">
                  <ShieldCheck className="w-4 h-4 text-accent flex-shrink-0" aria-hidden="true" data-id="element-181" />
                  <span className="font-extrabold text-nav font-heading text-sm leading-none" data-id="element-182">
                    {b.label}
                  </span>
                  <span className="text-xs text-gray-500 leading-none hidden sm:inline" data-id="element-183">
                    {b.sub}
                  </span>
                </div>)}
            </div>

            {/* Training includes */}
            <div className="mb-10" data-id="element-184">
              <h3 className="text-sm font-bold text-nav font-heading uppercase tracking-widest mb-4" data-id="element-185">
                What training includes
              </h3>
              <ul className="space-y-3" data-id="element-186">
                {bullets.map(b => <li key={b} className="flex items-center gap-3" data-id="element-187">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" aria-hidden="true" data-id="element-188" />
                    <span className="text-gray-700 font-medium" data-id="element-189">{b}</span>
                  </li>)}
              </ul>
            </div>

            {/* CTA Row */}
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-190">
              <a href="#signup" data-id="element-191">
                <Button variant="primary" size="lg" data-id="element-192">
                  Get One Week Free Trial
                </Button>
              </a>
              <a href="tel:8472128045" data-id="element-193">
                <Button variant="default" size="lg" data-id="element-194">
                  Call (847) 212-8045
                </Button>
              </a>
            </div>

            {/* Training photo — below CTAs on desktop */}
            <div className="mt-10 rounded-lg overflow-hidden shadow-md hidden lg:block" data-id="element-195">
              <img src="https://cdn.magicpatterns.com/uploads/aJpq2QmtoAqrwouJim2RWn/625075457_1464988238968623_5999708067650713080_n.jpg" alt="Dragon Martial Arts of Round Lake — students training on the mat" className="w-full h-56 object-cover object-top" data-id="element-196" />
            </div>
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="w-full lg:w-[420px] flex-shrink-0" data-id="element-197">
            <div className="bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden" data-id="element-198">
              {/* Form header */}
              <div className="bg-nav px-8 py-6" data-id="element-199">
                <h3 className="text-xl font-extrabold text-white font-heading uppercase tracking-tight" data-id="element-200">
                  Request Your Free Week
                </h3>
                <p className="text-gray-400 text-sm mt-1" data-id="element-201">
                  Prefer to talk?{' '}
                  <a href="tel:8472128045" className="text-accent font-bold hover:underline" data-id="element-202">
                    Call (847) 212-8045
                  </a>
                </p>
              </div>

              {submitted ? <div className="px-8 py-14 text-center" data-id="element-203">
                  <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-204">
                    <Send className="w-7 h-7 text-green-600" aria-hidden="true" data-id="element-205" />
                  </div>
                  <h4 className="text-xl font-bold text-nav font-heading mb-2" data-id="element-206">
                    Thank You!
                  </h4>
                  <p className="text-gray-600 mb-6 text-sm" data-id="element-207">
                    Your request has been received. We look forward to seeing
                    you on the mat.
                  </p>
                  <a href="tel:8472128045" className="text-link font-bold hover:text-accent text-sm" data-id="element-208">
                    (847) 212-8045
                  </a>
                </div> : <form onSubmit={handleSubmit} className="px-8 py-8 space-y-5" data-id="element-209">
                  {/* Name */}
                  <div data-id="element-210">
                    <label htmlFor="req-name" className="block text-xs font-bold text-nav font-heading uppercase tracking-wide mb-1.5" data-id="element-211">
                      Name <span className="text-accent" data-id="element-212">*</span>
                    </label>
                    <input id="req-name" name="name" type="text" required value={form.name} onChange={handleChange} placeholder="Your full name" className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors" data-id="element-213" />
                  </div>

                  {/* Email */}
                  <div data-id="element-214">
                    <label htmlFor="req-email" className="block text-xs font-bold text-nav font-heading uppercase tracking-wide mb-1.5" data-id="element-215">
                      Email <span className="text-accent" data-id="element-216">*</span>
                    </label>
                    <input id="req-email" name="email" type="email" required value={form.email} onChange={handleChange} placeholder="your@email.com" className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors" data-id="element-217" />
                  </div>

                  {/* Phone */}
                  <div data-id="element-218">
                    <label htmlFor="req-phone" className="block text-xs font-bold text-nav font-heading uppercase tracking-wide mb-1.5" data-id="element-219">
                      Phone <span className="text-accent" data-id="element-220">*</span>
                    </label>
                    <input id="req-phone" name="phone" type="tel" required value={form.phone} onChange={handleChange} placeholder="(555) 000-0000" className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors" data-id="element-221" />
                  </div>

                  {/* Program */}
                  <div data-id="element-222">
                    <label htmlFor="req-program" className="block text-xs font-bold text-nav font-heading uppercase tracking-wide mb-1.5" data-id="element-223">
                      Interested Program <span className="text-accent" data-id="element-224">*</span>
                    </label>
                    <select id="req-program" name="program" required value={form.program} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors" data-id="element-225">
                      <option value="" data-id="element-226">Select a program…</option>
                      {programs.map(p => <option key={p} value={p} data-id="element-227">
                          {p}
                        </option>)}
                    </select>
                  </div>

                  {/* Message */}
                  <div data-id="element-228">
                    <label htmlFor="req-message" className="block text-xs font-bold text-nav font-heading uppercase tracking-wide mb-1.5" data-id="element-229">
                      Message{' '}
                      <span className="text-gray-400 font-normal normal-case" data-id="element-230">
                        (optional)
                      </span>
                    </label>
                    <textarea id="req-message" name="message" rows={3} value={form.message} onChange={handleChange} placeholder="Any questions or details…" className="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-colors resize-none" data-id="element-231" />
                  </div>

                  {/* Submit */}
                  <Button type="submit" variant="primary" size="lg" fullWidth className="flex items-center justify-center gap-2" data-id="element-232">
                    <Send className="w-4 h-4" aria-hidden="true" data-id="element-233" />
                    Request Information
                  </Button>

                  <p className="text-center text-sm text-gray-500" data-id="element-234">
                    Or call{' '}
                    <a href="tel:8472128045" className="text-link font-bold hover:text-accent" data-id="element-235">
                      (847) 212-8045
                    </a>
                  </p>
                </form>}

              {/* Visit Us mini-card footer */}
              <div className="bg-gray-50 border-t border-gray-200 px-8 py-5 space-y-3" data-id="element-236">
                <h4 className="text-xs font-bold text-nav font-heading uppercase tracking-widest mb-3" data-id="element-237">
                  Visit Us
                </h4>
                <div className="flex items-start gap-2 text-sm text-gray-600" data-id="element-238">
                  <MapPin className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-239" />
                  <span data-id="element-240">655 Railroad Ave., Round Lake, IL 60073</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-241">
                  <Phone className="w-4 h-4 text-accent flex-shrink-0" aria-hidden="true" data-id="element-242" />
                  <a href="tel:8472128045" className="text-link font-bold hover:text-accent" data-id="element-243">
                    (847) 212-8045
                  </a>
                </div>
                <div className="flex items-start gap-2 text-sm text-gray-600" data-id="element-244">
                  <Clock className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden="true" data-id="element-245" />
                  <span data-id="element-246">
                    Mon–Fri 3–8pm &nbsp;·&nbsp; Sat 9am–1pm &nbsp;·&nbsp; Sun
                    Closed
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>;
}